import { useCallback, useMemo, useState } from "react";
import { catchError, of, tap } from "rxjs";

import { LibraryCategoriesStore } from "@/app/library/views/categories/data_sources/store";
import { LibraryTemplatesStore } from "@/app/library/views/templates/data_sources/store";
import { LibraryVerticalsStore } from "@/app/library/views/verticals/data_sources/store";
import { APILibraryTemplate } from "@/core/data_sources/models/APILibraryTemplate";
import { Button } from "@/core/ui/atoms";
import {
  adminInputClasses,
  adminSelectClasses,
  Banner,
} from "@/core/ui/molecules";
import { useStore } from "@/core/utils/hooks";
import { ErrorResponse } from "@/core/utils/request";

export type ImportedTemplateDraft = {
  readonly category: string;
  readonly description: string;
  readonly fields: number;
  readonly name: string;
  readonly templateId?: number;
  readonly type: "Form" | "Media" | "Model";
  readonly verticals: readonly string[];
  readonly version: string;
};

type ImportTemplateDialogProps = {
  readonly onClose: () => void;
  readonly onImport: (draft: ImportedTemplateDraft) => void;
};

export function ImportTemplateDialog({
  onClose,
  onImport,
}: ImportTemplateDialogProps) {
  const { state: categoryState, hooks: categoryHooks } = useStore(
    LibraryCategoriesStore.instance,
  );
  const { actions, isConnected } = useStore(LibraryTemplatesStore.instance);
  const { state: verticalState, hooks: verticalHooks } = useStore(
    LibraryVerticalsStore.instance,
  );
  const [name, setName] = useState("");
  const [type, setType] = useState<"form" | "media" | "model">("form");
  const [version, setVersion] = useState("v1.0");
  const [category, setCategory] = useState("");
  const [vertical, setVertical] = useState("");
  const [jsonDefinition, setJsonDefinition] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  categoryHooks.useCategoryStoreService({
    page: 1,
    page_size: 1000,
  });
  verticalHooks.useVerticalStoreService({
    page: 1,
    page_size: 1000,
  });

  const categories =
    categoryState?.list.map((item) => item.getData()).filter(Boolean) ?? [];
  const verticals =
    verticalState?.list.map((item) => item.getData()).filter(Boolean) ?? [];
  const selectedCategory =
    category || categories.find((item) => item.name)?.name || "";
  const selectedVertical =
    vertical || verticals.find((item) => item.name)?.name || "";
  const selectedVerticalItem = verticals.find(
    (item) => item.name === selectedVertical || item.code === selectedVertical,
  );
  const selectedVerticalCode =
    selectedVerticalItem?.code ??
    selectedVerticalItem?.name ??
    selectedVertical;
  const canSubmit = useMemo(
    () => isConnected && !!name.trim() && !!jsonDefinition.trim() && !saving,
    [isConnected, jsonDefinition, name, saving],
  );

  const handleSubmit = useCallback(() => {
    if (!canSubmit) {
      setError("Name and JSON definition are required.");
      return;
    }

    let parsedJson: Record<string, unknown>;
    try {
      const parsed = JSON.parse(jsonDefinition);
      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
        setError("JSON definition must be an object.");
        return;
      }
      parsedJson = parsed as Record<string, unknown>;
    } catch {
      setError("JSON definition is not valid JSON.");
      return;
    }

    const template = new APILibraryTemplate({
      name: name.trim(),
      type,
      payload_json: {
        ...parsedJson,
        categories: normalizeStringList(
          parsedJson.categories ?? parsedJson.category,
          selectedCategory,
        ),
        name: parsedJson.name ?? name.trim(),
        version: parsedJson.version ?? version,
        verticals: normalizeStringList(
          parsedJson.verticals,
          selectedVerticalCode,
        ),
      },
    });

    if (!template.isDataValidModel()) {
      setError(template.getModelValidationErrors().join(" "));
      return;
    }

    const request = template.toRequestModel();

    setSaving(true);
    setError("");
    actions
      .importTemplate(request)
      .pipe(
        tap((response) => {
          const imported = getImportedTemplateData(response);
          onImport({
            category: selectedCategory,
            description:
              getStringValue(request.json.description) ||
              "Imported JSON template draft waiting for approval.",
            fields: getFieldsCount(request.json),
            name: getStringValue(imported?.name) || name.trim(),
            templateId: imported?.id,
            type: toReviewQueueType(type),
            verticals: [selectedVertical].filter(Boolean),
            version,
          });
          onClose();
        }),
        catchError((requestError) => {
          setError(
            requestError instanceof ErrorResponse
              ? requestError.error.message
              : requestError instanceof Error
                ? requestError.message
                : "Unable to import template.",
          );
          return of(null);
        }),
        tap(() => setSaving(false)),
      )
      .subscribe();
  }, [
    actions,
    canSubmit,
    jsonDefinition,
    name,
    onClose,
    onImport,
    selectedCategory,
    selectedVertical,
    selectedVerticalCode,
    type,
    version,
  ]);

  return (
    <div className="flex max-h-[calc(100vh-7.5rem)] w-160 max-w-[96vw] flex-col overflow-hidden rounded-dialog bg-surface shadow-dialog">
      <header className="flex shrink-0 items-start justify-between gap-4 border-b border-border px-5 py-5">
        <div>
          <h2 className="text-lg font-extrabold text-ink">
            Import Template JSON
          </h2>
          <p className="mt-1 text-[13px] text-ink-muted">
            Creates a Draft template in the Global Library.
          </p>
        </div>
        <button
          className="text-2xl text-ink-subtle"
          onClick={onClose}
          type="button"
        >
          ×
        </button>
      </header>
      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <label>
            <span className="mb-1.5 block text-xs font-extrabold text-ink-muted">
              Type
            </span>
            <select
              className={`${adminSelectClasses} w-full`}
              onChange={(event) =>
                setType(event.target.value as "form" | "media" | "model")
              }
              value={type}
            >
              <option value="form">Form</option>
              <option value="media">Media</option>
              <option value="model">Model</option>
            </select>
          </label>
          <label>
            <span className="mb-1.5 block text-xs font-extrabold text-ink-muted">
              Version
            </span>
            <input
              className={`${adminInputClasses} w-full`}
              onChange={(event) => setVersion(event.target.value)}
              value={version}
            />
          </label>
        </div>
        <label className="mt-3 block">
          <span className="mb-1.5 block text-xs font-extrabold text-ink-muted">
            Template name
          </span>
          <input
            className={`${adminInputClasses} w-full`}
            onChange={(event) => setName(event.target.value)}
            placeholder="Template name"
            value={name}
          />
        </label>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <label className="mt-3 block">
            <span className="mb-1.5 block text-xs font-extrabold text-ink-muted">
              Category
            </span>
            <select
              className={`${adminSelectClasses} w-full`}
              onChange={(event) => setCategory(event.target.value)}
              value={selectedCategory}
            >
              {categories
                .filter((item) => item.name)
                .map((item) => (
                  <option key={item.id ?? item.name} value={item.name!}>
                    {item.name}
                  </option>
                ))}
            </select>
          </label>
          <label className="mt-3 block">
            <span className="mb-1.5 block text-xs font-extrabold text-ink-muted">
              Vertical
            </span>
            <select
              className={`${adminSelectClasses} w-full`}
              onChange={(event) => setVertical(event.target.value)}
              value={selectedVertical}
            >
              {verticals
                .filter((item) => item.name)
                .map((item) => (
                  <option
                    key={item.id ?? item.code ?? item.name}
                    value={item.name!}
                  >
                    {item.name}
                  </option>
                ))}
            </select>
          </label>
        </div>
        <label className="mt-3 block">
          <span className="mb-1.5 block text-xs font-extrabold text-ink-muted">
            JSON definition
          </span>
          <textarea
            className={`${adminInputClasses} min-h-24 w-full font-mono text-xs`}
            onChange={(event) => setJsonDefinition(event.target.value)}
            placeholder={getTemplateJsonPlaceholder(type)}
            value={jsonDefinition}
          />
        </label>
        {error ? <p className="mt-3 text-xs text-critical">{error}</p> : null}
        <Banner>
          <strong>Validation:</strong> form requires sectionsData or questions;
          media requires shots; model requires keyFields with external_id and
          label.
        </Banner>
      </div>
      <footer className="flex shrink-0 justify-end gap-2 border-t border-border bg-panel-muted px-5 py-3.5">
        <Button disabled={saving} onClick={onClose}>
          Cancel
        </Button>
        <Button disabled={!canSubmit} onClick={handleSubmit} variant="primary">
          {saving ? "Importing..." : "Validate & Import Draft"}
        </Button>
      </footer>
    </div>
  );
}

function getFieldsCount(json: Record<string, unknown>) {
  const fields = json.fields;
  const sectionsData = json.sectionsData;
  const questions = json.questions;
  const shots = json.shots;
  const keyFields = json.keyFields;

  if (Array.isArray(fields)) return fields.length;
  if (Array.isArray(sectionsData)) return sectionsData.length;
  if (Array.isArray(questions)) return questions.length;
  if (Array.isArray(shots)) return shots.length;
  if (Array.isArray(keyFields)) return keyFields.length;
  return 0;
}

function getImportedTemplateData(response: unknown) {
  if (!response || typeof response !== "object" || !("data" in response)) {
    return null;
  }

  const data = (response as { data?: unknown }).data;
  if (!data || typeof data !== "object") {
    return null;
  }

  return data as { id?: number; name?: unknown };
}

function getStringValue(value: unknown) {
  return typeof value === "string" ? value : "";
}

function toReviewQueueType(type: "form" | "media" | "model") {
  const typeMap = {
    form: "Form",
    media: "Media",
    model: "Model",
  } as const;

  return typeMap[type];
}

function normalizeStringList(value: unknown, fallback: string) {
  if (Array.isArray(value)) {
    return value
      .filter((item): item is string => typeof item === "string")
      .filter(Boolean);
  }
  if (typeof value === "string" && value.trim()) {
    return [value.trim()];
  }
  return fallback ? [fallback] : [];
}

function getTemplateJsonPlaceholder(type: "form" | "media" | "model") {
  const placeholders = {
    form: `{
  "name": "Site Audit Form",
  "sectionsData": [
    {
      "title": "General Info",
      "fields": [
        { "label": "Site Name", "type": "text" }
      ]
    }
  ]
}`,
    media: `{
  "name": "Tower Photos",
  "shots": [
    { "name": "Front", "type": "picture" }
  ]
}`,
    model: `{
  "name": "Equipment Model",
  "keyFields": [
    {
      "external_id": "serial",
      "label": "Serial Number"
    }
  ]
}`,
  } as const;

  return placeholders[type];
}
